/**
 * Campaigns Management - Organization's advertising campaigns
 * CRUD operations for campaigns linked to sponsors
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Megaphone,
  Plus,
  Search,
  Filter,
  Calendar,
  DollarSign,
  Target,
  BarChart3,
  Loader2,
  MoreVertical,
  Eye,
  Edit2,
  Trash2,
  Play,
  Pause,
  CheckCircle,
  XCircle,
  Clock,
  Image,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Campaign status config
const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30', icon: Clock },
  pending_approval: { label: 'Pending', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Clock },
  approved: { label: 'Approved', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30', icon: CheckCircle },
  active: { label: 'Active', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: Play },
  paused: { label: 'Paused', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: Pause },
  completed: { label: 'Completed', color: 'bg-violet-500/20 text-violet-400 border-violet-500/30', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-rose-500/20 text-rose-400 border-rose-500/30', icon: XCircle },
};

// Campaign types
const CAMPAIGN_TYPES = [
  { value: 'banner', label: 'Banner Ad', description: 'Display banners on event pages' },
  { value: 'discovery', label: 'Discovery Promo', description: 'Promoted listings in discovery feed' },
  { value: 'sponsored_event', label: 'Sponsored Event', description: 'Event sponsorship branding' },
  { value: 'push_notification', label: 'Push Notification', description: 'Targeted push notifications' },
];

// Glass Panel component
const GlassPanel = ({ children, className = '', ...props }) => (
  <div
    className={cn(
      'bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] rounded-2xl',
      className
    )}
    {...props}
  >
    {children}
  </div>
);

// Campaign Card Component
const CampaignCard = ({ campaign, onView, onEdit, onStatusChange }) => {
  const statusConfig = STATUS_CONFIG[campaign.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusConfig.icon;
  const typeInfo = CAMPAIGN_TYPES.find(t => t.value === campaign.campaign_type) || CAMPAIGN_TYPES[0];

  const formatDate = (date) => {
    if (!date) return 'Not set';
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <GlassPanel
      className="p-5 hover:border-white/[0.12] transition-all cursor-pointer group"
      onClick={() => onView?.(campaign)}
      data-testid={`campaign-card-${campaign.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
            <Megaphone className="w-5 h-5 text-violet-400" />
          </div>
          <div>
            <h3 className="font-semibold text-cloud-white">{campaign.campaign_name}</h3>
            <p className="text-xs text-muted-text">{typeInfo.label}</p>
          </div>
        </div>
        <Badge className={cn('border', statusConfig.color)}>
          <StatusIcon className="w-3 h-3 mr-1" />
          {statusConfig.label}
        </Badge>
      </div>

      {campaign.description && (
        <p className="text-sm text-soft-silver mb-4 line-clamp-2">{campaign.description}</p>
      )}

      <div className="grid grid-cols-3 gap-4 text-center mb-4">
        <div>
          <p className="text-lg font-mono font-semibold text-cloud-white">
            ${(campaign.total_budget || 0).toLocaleString()}
          </p>
          <p className="text-xs text-muted-text">Budget</p>
        </div>
        <div>
          <p className="text-lg font-mono font-semibold text-cloud-white">
            ${(campaign.spent_budget || 0).toLocaleString()}
          </p>
          <p className="text-xs text-muted-text">Spent</p>
        </div>
        <div>
          <p className="text-lg font-mono font-semibold text-cyan-400">
            {campaign.impressions || 0}
          </p>
          <p className="text-xs text-muted-text">Impressions</p>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
        <div className="flex items-center gap-2 text-xs text-muted-text">
          <Calendar className="w-3 h-3" />
          {formatDate(campaign.start_date)} - {formatDate(campaign.end_date)}
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={(e) => { e.stopPropagation(); onEdit?.(campaign); }}
          >
            <Edit2 className="w-4 h-4 text-white/50 hover:text-white" />
          </Button>
          {campaign.status === 'active' && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={(e) => { e.stopPropagation(); onStatusChange?.(campaign, 'paused'); }}
            >
              <Pause className="w-4 h-4 text-orange-400" />
            </Button>
          )}
          {campaign.status === 'paused' && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={(e) => { e.stopPropagation(); onStatusChange?.(campaign, 'active'); }}
            >
              <Play className="w-4 h-4 text-emerald-400" />
            </Button>
          )}
        </div>
      </div>
    </GlassPanel>
  );
};

// Campaign Form Component
const CampaignForm = ({ campaign, sponsors, onSubmit, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    campaign_name: campaign?.campaign_name || '',
    campaign_type: campaign?.campaign_type || 'banner',
    sponsor_id: campaign?.sponsor_id || '',
    description: campaign?.description || '',
    start_date: campaign?.start_date?.split('T')[0] || '',
    end_date: campaign?.end_date?.split('T')[0] || '',
    total_budget: campaign?.total_budget || 0,
    daily_budget: campaign?.daily_budget || 0,
    notes: campaign?.notes || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.campaign_name.trim()) {
      toast.error('Campaign name is required');
      return;
    }
    if (!formData.sponsor_id) {
      toast.error('Please select a sponsor');
      return;
    }
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label htmlFor="campaign_name">Campaign Name *</Label>
          <Input
            id="campaign_name"
            value={formData.campaign_name}
            onChange={(e) => setFormData({ ...formData, campaign_name: e.target.value })}
            placeholder="Summer Promo 2026"
            className="mt-1.5 bg-white/[0.03] border-white/[0.08]"
          />
        </div>

        <div>
          <Label htmlFor="sponsor_id">Sponsor *</Label>
          <Select
            value={formData.sponsor_id}
            onValueChange={(val) => setFormData({ ...formData, sponsor_id: val })}
          >
            <SelectTrigger className="mt-1.5 bg-white/[0.03] border-white/[0.08]">
              <SelectValue placeholder="Select a sponsor" />
            </SelectTrigger>
            <SelectContent>
              {sponsors.map((sponsor) => (
                <SelectItem key={sponsor.id} value={sponsor.id}>
                  {sponsor.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="campaign_type">Campaign Type</Label>
          <Select
            value={formData.campaign_type}
            onValueChange={(val) => setFormData({ ...formData, campaign_type: val })}
          >
            <SelectTrigger className="mt-1.5 bg-white/[0.03] border-white/[0.08]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CAMPAIGN_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  <div>
                    <span>{type.label}</span>
                    <span className="text-xs text-muted-text ml-2">- {type.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Campaign goals and details..."
            rows={3}
            className="mt-1.5 bg-white/[0.03] border-white/[0.08]"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="start_date">Start Date</Label>
            <Input
              id="start_date"
              type="date"
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              className="mt-1.5 bg-white/[0.03] border-white/[0.08]"
            />
          </div>
          <div>
            <Label htmlFor="end_date">End Date</Label>
            <Input
              id="end_date"
              type="date"
              value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              className="mt-1.5 bg-white/[0.03] border-white/[0.08]"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="total_budget">Total Budget ($)</Label>
            <Input
              id="total_budget"
              type="number"
              min="0"
              step="0.01"
              value={formData.total_budget}
              onChange={(e) => setFormData({ ...formData, total_budget: parseFloat(e.target.value) || 0 })}
              className="mt-1.5 bg-white/[0.03] border-white/[0.08]"
            />
          </div>
          <div>
            <Label htmlFor="daily_budget">Daily Budget ($)</Label>
            <Input
              id="daily_budget"
              type="number"
              min="0"
              step="0.01"
              value={formData.daily_budget}
              onChange={(e) => setFormData({ ...formData, daily_budget: parseFloat(e.target.value) || 0 })}
              className="mt-1.5 bg-white/[0.03] border-white/[0.08]"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="notes">Internal Notes</Label>
          <Textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Internal notes about this campaign..."
            rows={2}
            className="mt-1.5 bg-white/[0.03] border-white/[0.08]"
          />
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1 border-white/[0.08]">
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading} className="flex-1 bg-violet-500 hover:bg-violet-600">
          {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {campaign ? 'Update Campaign' : 'Create Campaign'}
        </Button>
      </div>
    </form>
  );
};

// Stats Card
const StatsCard = ({ label, value, icon: Icon, trend, color = 'violet' }) => (
  <div className="p-4 bg-white/[0.02] rounded-xl border border-white/[0.06]">
    <div className="flex items-center justify-between mb-2">
      <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', `bg-${color}-500/20`)}>
        <Icon className={cn('w-4 h-4', `text-${color}-400`)} />
      </div>
      {trend && (
        <span className={cn('text-xs font-mono', trend > 0 ? 'text-emerald-400' : 'text-rose-400')}>
          {trend > 0 ? '+' : ''}{trend}%
        </span>
      )}
    </div>
    <p className="text-2xl font-bold font-mono text-cloud-white">{value}</p>
    <p className="text-xs text-muted-text mt-1">{label}</p>
  </div>
);

// Main Campaigns Page
export const Campaigns = () => {
  const { session, organization, loading: authLoading } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const organizationId = organization?.id;

  // Fetch campaigns and sponsors
  const fetchData = useCallback(async () => {
    // Wait for auth to complete
    if (authLoading) {
      console.log('Campaigns: Auth still loading...');
      return;
    }
    
    if (!session?.access_token || !organizationId) {
      console.log('Campaigns: Missing org or token', { org: organizationId, token: !!session?.access_token });
      setLoading(false);
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${session.access_token}` };
      console.log('Campaigns: Fetching from API for org:', organizationId);

      const [campaignsRes, sponsorsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/campaigns?organization_id=${organizationId}`, { headers }),
        fetch(`${API_URL}/api/org-sponsors?organization_id=${organizationId}`, { headers }),
        fetch(`${API_URL}/api/campaigns/stats?organization_id=${organizationId}`, { headers }),
      ]);

      console.log('Campaigns API responses:', campaignsRes.ok, sponsorsRes.ok, statsRes.ok);

      if (campaignsRes.ok) {
        const data = await campaignsRes.json();
        console.log('Campaigns data:', data.campaigns?.length);
        setCampaigns(data.campaigns || []);
      } else {
        console.error('Campaigns response error:', await campaignsRes.text());
      }

      if (sponsorsRes.ok) {
        const data = await sponsorsRes.json();
        console.log('Sponsors data:', data.sponsors?.length);
        setSponsors(data.sponsors || []);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [session?.access_token, organizationId, authLoading]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Create/Update campaign
  const handleSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      const headers = {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      };

      const url = selectedCampaign
        ? `${API_URL}/api/campaigns/${selectedCampaign.id}`
        : `${API_URL}/api/campaigns?organization_id=${organizationId}`;

      const method = selectedCampaign ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(formData),
      });

      if (res.ok) {
        toast.success(selectedCampaign ? 'Campaign updated!' : 'Campaign created!');
        setIsFormOpen(false);
        setSelectedCampaign(null);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save campaign');
      }
    } catch (error) {
      toast.error('Failed to save campaign');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update campaign status
  const handleStatusChange = async (campaign, newStatus) => {
    try {
      const res = await fetch(
        `${API_URL}/api/campaigns/${campaign.id}/status?status=${newStatus}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${session.access_token}` },
        }
      );

      if (res.ok) {
        toast.success(`Campaign ${newStatus === 'active' ? 'activated' : 'paused'}`);
        fetchData();
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Filter campaigns
  const filteredCampaigns = campaigns.filter((campaign) => {
    const matchesSearch = campaign.campaign_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
          <p className="text-soft-silver text-sm">Loading campaigns...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-cloud-white flex items-center gap-3">
            <Megaphone className="w-7 h-7 text-violet-400" />
            Campaigns
          </h1>
          <p className="text-soft-silver text-sm mt-1">
            Manage advertising campaigns for your sponsors
          </p>
        </div>
        <Button
          onClick={() => { setSelectedCampaign(null); setIsFormOpen(true); }}
          className="bg-violet-500 hover:bg-violet-600"
          data-testid="create-campaign-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Campaign
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatsCard label="Total Campaigns" value={stats.total || 0} icon={Megaphone} color="violet" />
          <StatsCard label="Active" value={stats.active || 0} icon={Play} color="emerald" />
          <StatsCard label="Total Budget" value={`$${(stats.total_budget || 0).toLocaleString()}`} icon={DollarSign} color="cyan" />
          <StatsCard label="Total Spent" value={`$${(stats.total_spent || 0).toLocaleString()}`} icon={BarChart3} color="amber" />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-text" />
          <Input
            placeholder="Search campaigns..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white/[0.03] border-white/[0.08]"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] bg-white/[0.03] border-white/[0.08]">
            <Filter className="w-4 h-4 mr-2 text-muted-text" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Campaigns Grid */}
      {filteredCampaigns.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCampaigns.map((campaign) => (
            <CampaignCard
              key={campaign.id}
              campaign={campaign}
              onView={(c) => { setSelectedCampaign(c); setIsFormOpen(true); }}
              onEdit={(c) => { setSelectedCampaign(c); setIsFormOpen(true); }}
              onStatusChange={handleStatusChange}
            />
          ))}
        </div>
      ) : (
        <GlassPanel className="p-12 text-center">
          <Megaphone className="w-14 h-14 text-white/20 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-cloud-white mb-2">
            {searchQuery || statusFilter !== 'all' ? 'No campaigns match your filters' : 'No campaigns yet'}
          </h3>
          <p className="text-soft-silver text-sm mb-6">
            {sponsors.length === 0
              ? 'Add sponsors first, then create campaigns to run ads.'
              : 'Create your first campaign to start running ads for your sponsors.'}
          </p>
          {sponsors.length > 0 && (
            <Button onClick={() => setIsFormOpen(true)} className="bg-violet-500 hover:bg-violet-600">
              <Plus className="w-4 h-4 mr-2" />
              Create Campaign
            </Button>
          )}
        </GlassPanel>
      )}

      {/* Campaign Form Sheet */}
      <Sheet open={isFormOpen} onOpenChange={setIsFormOpen}>
        <SheetContent className="bg-obsidian border-white/[0.06] w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-cloud-white">
              {selectedCampaign ? 'Edit Campaign' : 'Create Campaign'}
            </SheetTitle>
            <SheetDescription className="text-soft-silver">
              {selectedCampaign
                ? 'Update campaign details and settings.'
                : 'Set up a new advertising campaign for your sponsor.'}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            {sponsors.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-soft-silver mb-4">
                  You need to add a sponsor before creating a campaign.
                </p>
                <Button variant="outline" onClick={() => window.location.href = '/app/sponsors'}>
                  Go to Sponsors
                </Button>
              </div>
            ) : (
              <CampaignForm
                campaign={selectedCampaign}
                sponsors={sponsors}
                onSubmit={handleSubmit}
                onCancel={() => { setIsFormOpen(false); setSelectedCampaign(null); }}
                isLoading={isSubmitting}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Campaigns;
